import React, { useEffect, useMemo, useState } from 'react';

import {
  AdornedInput,
  Adornment,
  Anchor,
  Combobox,
  DetailedIconHammer,
  Flex,
  Form,
  FormField,
  FormHelp,
  Input,
  Label,
  Select,
  Spacer,
  Textarea,
} from '@zillow/constellation';

import { PartialMessage } from '@zg-rentals/backend-styled-components';
import { useRentalManagerUser, useRmwPaths } from '../../hooks';
import type { AutoCompleteAddress, SupportRequestV2Dto } from './actions';
import { getAddressAutocompleteOptions, submitGuestRequestV2, submitUserRequestV2 } from './actions';
import type { FormStateType } from './SubmitRequestPage';
import type {
  GetHelpTopicListResponseV2Dto,
  HelpCategoryDto,
  HelpTopicV2Dto,
  PartialMessageTextAndUrlDto,
  RentalManagerAuthCheckUserInfoDto,
} from '@zg-rentals/ts-rental-manager-api-client';
import { LoadingConstants } from '../..';

type SubmitRequestFormProps = {
  onSubmit?: () => void;
  submitClicked: boolean;
  setCanSubmit: (val: boolean) => void;
  setRequestState: (loadingState: LoadingConstants) => void;
  helpTopics: GetHelpTopicListResponseV2Dto;
  formState: FormStateType;
  setFormState: (state: FormStateType) => void;
  setErrorAlert: (err: string) => void;
  zrmwUser?: RentalManagerAuthCheckUserInfoDto;
  queryObject?: {
    categoryKey: string | null;
    topicKey: string | null;
    listingAddress: string | null;
    unit: string | null;
    listingAlias: string | null;
  };
  zrmSignInPage?: string;
};

export const SubmitRequestForm = ({
  onSubmit = () => {},
  setRequestState,
  setCanSubmit,
  setFormState,
  setErrorAlert,
  formState,
  helpTopics,
  submitClicked,
  zrmwUser,
  queryObject,
  zrmSignInPage,
}: SubmitRequestFormProps) => {
  const { paths } = useRmwPaths();
  const nonZrmwUser = useRentalManagerUser().user;
  const user = zrmwUser || nonZrmwUser;

  const [email, setEmail] = useState(user.email);
  const [listing, setListing] = useState<AutoCompleteAddress>();
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [addressOptions, setAddressOptions] = useState<Array<AutoCompleteAddress>>();
  const [addressOptionsState, setAddressOptionsState] = useState<LoadingConstants>(LoadingConstants.INITIAL);

  const { loggedIn } = user;
  const { category: selectedHelpCategory, topic: selectedHelpTopic, description, unit, typedAddress } = formState;

  const isAddressRequired = selectedHelpCategory?.key === 'myListing';
  const helpCategoryWasChosen = selectedHelpCategory !== undefined;
  const addressOptionsAreLoading =
    addressOptionsState !== LoadingConstants.SUCCESS && addressOptionsState !== LoadingConstants.ERROR;

  const canSubmit = useMemo<boolean>(() => {
    if (isAddressRequired && ((listing === undefined && typedAddress === '') || !loggedIn)) return false;
    return description !== '' && Boolean(email) && helpCategoryWasChosen && selectedHelpTopic?.key !== '';
  }, [
    isAddressRequired,
    listing,
    typedAddress,
    loggedIn,
    description,
    email,
    helpCategoryWasChosen,
    selectedHelpTopic?.key,
  ]);

  useEffect(() => {
    setCanSubmit(canSubmit);
  }, [canSubmit, setCanSubmit]);

  const addUnitToAddress = (address: string) => {
    if (!unit) return address;
    const addressArray = address.split(',');
    addressArray[0] += ` #${unit}`;
    return addressArray.join(',');
  };

  const handleSelectAddress = (selectedListing: AutoCompleteAddress) => {
    setListing(selectedListing);
    setFormState({ ...formState, typedAddress: selectedListing.name });
  };

  const handleGetAddressOptions = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormState({ ...formState, typedAddress: e.target.value });
    if (e.target.value.length < 7) return;

    setAddressOptionsState(LoadingConstants.LOADING);
    getAddressAutocompleteOptions(
      { partialAddress: e.target.value },
      (res) => {
        setAddressOptionsState(LoadingConstants.SUCCESS);
        return setAddressOptions(res.addresses);
      },
      () => setAddressOptionsState(LoadingConstants.ERROR),
    );
  };

  const handleSelectCategory = (value: string) => {
    const selectedCategory = helpTopics?.categories?.find((category: HelpCategoryDto) => category.key === value);
    if (selectedCategory) {
      setFormState({
        ...formState,
        topic: { key: '', description: '', descriptionFieldPlaceholder: '' },
        category: selectedCategory,
      });
    }
  };

  const handleSelectTopic = (value: string) => {
    const selectedTopic = selectedHelpCategory?.topics?.find((topic: HelpTopicV2Dto) => topic.key === value);
    if (selectedTopic) {
      setFormState({ ...formState, topic: selectedTopic });
    }
  };

  const handleSubmitRequest = () => {
    const paramsV2 = {
      topic: selectedHelpTopic?.key,
      category: selectedHelpCategory?.key,
      email: email,
      description: description,
      listingAddress: addUnitToAddress(typedAddress),
      firstName: firstName || undefined,
      lastName: lastName || undefined,
    } as SupportRequestV2Dto;

    setRequestState(LoadingConstants.LOADING);
    if (!loggedIn) {
      submitGuestRequestV2(
        paramsV2,
        () => {
          setRequestState(LoadingConstants.SUCCESS);
          onSubmit();
        },
        (err) => {
          setRequestState(LoadingConstants.ERROR);
          let errorMesssage = 'An error has occurred while submitting';
          if (err?.message) errorMesssage += `: ${err.message}`;
          setErrorAlert(errorMesssage);
        },
      );
    } else {
      submitUserRequestV2(
        paramsV2,
        () => {
          setRequestState(LoadingConstants.SUCCESS);
          onSubmit();
        },
        (err) => {
          setRequestState(LoadingConstants.ERROR);
          let errorMesssage = 'An error has occurred while submitting';
          if (err?.message) errorMesssage += `: ${err.message}`;
          setErrorAlert(errorMesssage);
        },
      );
    }
  };

  if (submitClicked && canSubmit) handleSubmitRequest();

  return (
    <Form data-testid="SubmitRequestForm" onSubmit={(e: React.FormEvent) => e.preventDefault()}>
      <FormField
        label={<Label>{queryObject?.categoryKey ? 'Category' : 'I need help with'}</Label>}
        control={
          <Select
            aria-label="RequestType"
            name="requestType"
            value={selectedHelpCategory?.key}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleSelectCategory(e.target.value)}
            readOnly={queryObject?.categoryKey}
          >
            <option disabled={helpCategoryWasChosen} aria-label="Please select an option">
              -
            </option>

            {helpTopics?.categories?.map((category: HelpCategoryDto) => {
              return (
                <option key={category?.key} value={category?.key} aria-label={category?.description}>
                  {category?.description}
                </option>
              );
            })}
          </Select>
        }
        description={
          isAddressRequired && !loggedIn ? (
            <FormHelp>
              Please{' '}
              {paths || zrmSignInPage ? (
                <Anchor as="a" href={zrmSignInPage || paths.zrmSignInPage(`${paths.submitRequest}`)}>
                  <strong>sign in</strong>
                </Anchor>
              ) : (
                'sign in'
              )}{' '}
              to submit a request about your listing. If you are unable to sign in, select <b>My Account</b>.
            </FormHelp>
          ) : null
        }
        required={true}
      />
      {helpCategoryWasChosen &&
        helpTopics?.outages?.map((outage: Array<PartialMessageTextAndUrlDto>, i: number) => {
          return (
            <Flex
              display="flex"
              flexDirection="row"
              alignItems={{ default: 'flex-start', lg_gte: 'center' }}
              justifyContent="flex-start"
              paddingY="xs"
              key={`outages-content-${i}`}
            >
              <Spacer marginRight="xxs">
                <DetailedIconHammer size="sm" />
              </Spacer>
              {<PartialMessage partialMessages={outage} fontType="finePrint" />}
            </Flex>
          );
        })}
      {helpCategoryWasChosen && (
        <>
          <FormField
            label={<Label>{'Select the topic below that best fits your problem'}</Label>}
            control={
              <Select
                aria-label="RequestType"
                name="requestType"
                value={selectedHelpTopic?.key}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleSelectTopic(e.target.value)}
                readOnly={queryObject?.topicKey}
              >
                <option disabled={selectedHelpTopic?.key !== ''} aria-label="Please select an option">
                  -
                </option>

                {selectedHelpCategory?.topics?.map((topic: HelpTopicV2Dto) => {
                  return (
                    <option key={topic?.key} value={topic?.key} aria-label={topic?.description}>
                      {topic?.description}
                    </option>
                  );
                })}
              </Select>
            }
            required={true}
          />
          <Flex display="flex" flexDirection={{ default: 'column', lg: 'row' }}>
            <FormField
              marginRight={{ default: 0, lg: 'sm' }}
              style={{ width: '50%' }}
              label={<Label>First name</Label>}
              control={
                <Input
                  value={firstName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                />
              }
            />
            <FormField
              marginBottom="sm"
              style={{ width: '50%' }}
              label={<Label>Last name</Label>}
              control={
                <Input
                  value={lastName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                />
              }
            />
          </Flex>
          <FormField
            label={<Label>Email address</Label>}
            control={
              <Input
                placeholder="Type your email address here"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              />
            }
            required={true}
          />
          <Flex display="flex" flexDirection={{ default: 'column', lg: 'row' }}>
            <Flex flexBasis="70%">
              <FormField required={isAddressRequired} marginRight={{ default: 0, lg: 'sm' }}>
                <Label id="street-address-label">Listing address</Label>
                <Combobox
                  appearance="input"
                  loading={addressOptionsAreLoading}
                  options={
                    addressOptions?.map((option) => {
                      return {
                        ...option,
                        value: option.name,
                        label: option.name,
                      };
                    }) || []
                  }
                  placeholder="Enter an address here."
                  renderInput={() => {
                    return (
                      <Input
                        value={typedAddress}
                        onChange={handleGetAddressOptions}
                        aria-labelledby="street-address-label"
                        readOnly={queryObject?.listingAddress}
                      />
                    );
                  }}
                  onOptionSelect={handleSelectAddress}
                  freeform
                  showValueForLabel
                />
              </FormField>
            </Flex>
            <Flex marginBottom="sm" flexBasis="30%">
              <FormField
                label={<Label>Unit number</Label>}
                control={
                  <AdornedInput
                    leftAdornment={<Adornment>#</Adornment>}
                    input={
                      <Input
                        aria-labelledby="unit-number-label"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setFormState({ ...formState, unit: e.target.value })
                        }
                        value={unit}
                        readOnly={queryObject?.unit}
                      />
                    }
                  />
                }
              />
            </Flex>
          </Flex>
          <FormField
            label={<Label>Description</Label>}
            control={
              <Textarea
                value={description}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setFormState({ ...formState, description: e.target.value })
                }
                placeholder={
                  selectedHelpTopic?.descriptionFieldPlaceholder ||
                  'Describe your problem to help us better assist you.'
                }
              />
            }
            required={true}
          />
        </>
      )}
    </Form>
  );
};
